import { RefObject, useEffect, useState } from 'react';

import type { Suggestions } from '@mint-sdk/api-recommendations';

const WIDTH_OF_CARD = 280;

type Props = {
  data: Suggestions.ForecastedKPIItemType[];
  ref: RefObject<HTMLDivElement | null>;
};

export const useWidgetCarousel = ({ data, ref }: Props) => {
  const [startIndex, setStartIndex] = useState(0);
  const [countOfSliders, setCountOfSliders] = useState(0);

  useEffect(() => {
    if (!ref.current) return;

    const observer = new ResizeObserver((entries) => {
      const width = entries[0].contentRect.width;
      setCountOfSliders(Math.floor(width / WIDTH_OF_CARD));
    });

    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref]);

  const clampedStart =
    data.length <= countOfSliders
      ? 0
      : Math.min(startIndex, data.length - countOfSliders);
  const endIndex = Math.min(clampedStart + countOfSliders, data.length);
  const activeSliders = data.slice(clampedStart, endIndex);
  const needToLeft = clampedStart > 0;
  const needToRight = endIndex < data.length;

  const moveToRight = () => {
    setStartIndex((prev) => Math.min(prev + 1, data.length - countOfSliders));
  };

  const moveToLeft = () => {
    setStartIndex((prev) => Math.max(prev - 1, 0));
  };

  return {
    activeSliders,
    moveToRight,
    moveToLeft,
    needToLeft,
    needToRight,
  };
};
